<template>
  <ion-toolbar>
    <ion-buttons :class="['nav-buttons', ((isSimple || !showTitle) ? 'simple' : undefined)]" slot="start">
      <template v-if="!hideTitles">
        <ExtendableChip
          v-if="isSubpage"
          class="back-button"
          :color="confirmDiscard ? 'danger' : undefined"
          :title="(confirmDiscard ? (discardQuestion || i18n.$t('default_interaction.discard_question')) : undefined)"
          :extendOnClick="confirmDiscard"
          :forwardClick="!confirmDiscard"
          forwardExtendedClick
          transparentWhenClosed
          button
          textIcon
          @extendedClickEnabled="(newState) => discardConfirmationExtended = newState"
          v-model:extended="showDiscardConfirmation"
          :collapseTimeout="5000"
          :brightness="0.8"
          >
          <template v-slot:permanent>
            <ion-back-button class="inner-button" default-href="/health"></ion-back-button> <!-- TODO Use default-href based on the current path. /health/something -->
          </template>
          <template v-slot:default>
            <ion-label class="discard-label">{{ (confirmDiscard ? (discardQuestion || i18n.$t('default_interaction.discard_question')) : undefined) }}</ion-label>
          </template>
        </ExtendableChip>
        
        <ion-button class="logo-button" v-else href="/">
          <AnimatedLogo name="full-cycle" intensity="medium-soft" class="logo" duration="2500" :enabled="false" stopPattern="full" alt="App-Logo"></AnimatedLogo>
        </ion-button>
      </template>

      <ion-icon v-if="isOffline" id="offline-icon" :icon="cloudOfflineOutline"></ion-icon>
      <span v-if="titleIcon && !hideTitles" class="title-icon">
        <ion-icon :icon="titleIcon"></ion-icon>
      </span>
    </ion-buttons>
    <div v-if="!hideTitles" id="title" @click.prevent="(event) => (informationState != null) ? setInformationPopoverOpen(true, event) : undefined">
      <ion-label :class="(showTitle ? ((isSimple) ? 'simple-title' : 'custom-title') : 'fallback-title')">
        <slot>
          <span v-if="showTitle">{{ title }}</span>
          <template v-else>
            <span id="date-title" class="fl-s1 wl-light">{{currentTimeStrings.date}}</span>
            <span> <!-- Group time and separator together for wrapping -->
              <span class="date-time-separator fl-s1 wl-light">|</span>
              <span id="time-title" class="fl-s1 wl-light">{{currentTimeStrings.time}}</span>
            </span>
          </template>
        </slot>
      </ion-label>
      <div class="information-addon fl-s1 wl-light" v-if="!isSimple && informationState != null">
        <a class="sub-text" href="javascript:void(0);">
          <ion-icon :icon="informationState.icon || informationCircleOutline" :color="informationState.color"></ion-icon>
          <span>{{ informationState.title }}</span>
        </a>
        <ion-popover
          :is-open="isInformationPopoverOpen && informationState != null"
          css-class="information-addon-popover"
          :event="informationPopoverEventRef"
          :enterAnimation="iosEnterAnimation"
          :leaveAnimation="iosLeaveAnimation"
          @didDismiss="setInformationPopoverOpen(false)"
        >
          <ion-content v-if="informationState != null" class="information-addon-popover-content ion-padding">
            <ion-label class="wl-light">{{ (informationState != null) ? informationState.message : '' }}</ion-label>
            <template v-if="informationState != null && informationState.proofUploadRequired">
              <ion-item lines=full id="name" class="name-upload-proof-item">
                <ion-label position="stacked">{{ i18n.$t('login.name_proof') }}</ion-label>   
                <ion-input required type="text" name="name" :placeholder="i18n.$t('default_interaction.enter_text')" v-model="signupName" :maxlength="maxInputLength"></ion-input>
              </ion-item>
              <ion-item lines=full id="birthname" class="name-upload-proof-item">
                <ion-label position="stacked">{{ i18n.$t('login.birthname') }}</ion-label>
                <ion-input type="text" name="birthname" :placeholder="i18n.$t('login.birthname_optional')" v-model="signupBirthname" :maxlength="maxInputLength"></ion-input>
              </ion-item>
              <FormItem
                type="document"
                formKey="role-proof"
                :indexValue="requestedRole"
                :modelValue="roleProof"
                @update:modelValue="(value) => setRoleProof(value)"
                :display_name="i18n.$t(`login.choose_role.proof_info.${requestedRole}`)"
                class="proof-file-upload"
                allow_custom_values
                returnRawValue
                lines="none">
              </FormItem>
              <ion-label class="wl-light fl-s1" id="upload-proof-verfication-time">{{ i18n.$t(`login.choose_role.verification_time.before`) }} {{ verificationTimeHours }} {{ i18n.$t(`login.choose_role.verification_time.after`) }}</ion-label>

              <ion-label class="wl-light fl-s1" id="upload-proof-contact">
                {{ i18n.$t('login.choose_role.contact_questions.before') }}<a href="mailto:info@anirec.de">{{ i18n.$t('login.choose_role.contact_questions.contact') }}</a>{{ i18n.$t('login.choose_role.contact_questions.after') }}
              </ion-label>

              <ProgressButton class="upload-proof-confirm-button" @click="uploadProof()" color="primary" shape="round" :disabled="!isRoleProofSet || !isSignupNameSet" :loading="roleUploadLoading">
                {{ i18n.$t(`login.choose_role.confirm`) }}
              </ProgressButton>
            </template>
          </ion-content>
        </ion-popover>
      </div>
    </div>
    <ion-buttons v-if="!isSimple" class="menu-buttons" slot="end">
      <ion-button v-if="!hideUserActions" id="messages-button" style="display: none; pointer-events: none;">
        <ion-badge :class="(messagesCount <= 0) ? 'no-badge' : undefined" color="primary" mode="ios" class="solid">{{ messagesCount }}</ion-badge>
        <ion-icon :icon="mailOutline"></ion-icon>
      </ion-button>
      <ion-button v-if="!hideUserActions" id="drafts-button" @click="showAllDraftEntries()" style="display: none; pointer-events: none;">
        <ion-badge :class="(draftsCount <= 0) ? 'no-badge' : undefined" color="primary" mode="ios" class="solid">{{ draftsCount }}</ion-badge>
        <ion-icon :icon="createOutline"></ion-icon>
      </ion-button>
      <CodeScannerButton v-if="!hideUserActions" class="code-scanner-button" @codeScanned="handleScannedCode"></CodeScannerButton>
      <ExtendableChip
        class="menu-button-notification"
        :color="(notificationCount >= 1) ? 'primary' : undefined"
        extendToLeft
        :transparentWhenClosed="notificationCount < 1"
        forwardClick
        forwardExtendedClick
        textIcon
        button
        v-model:extended="showNotifications"
        :closeOnOutsideClick="false"
        :collapseTimeout="5000"
        :brightness="0.8"
        >
        <template v-slot:permanent>
          <ion-button class="inner-button menu" v-if="!isMenuDisabled" @click="openSettings">
            <ion-icon slot=icon-only :icon="hamburgerIcon"></ion-icon>
          </ion-button>
          <!-- If the menu is disabled, show some settings in the toolbar-->
          <ion-button class="inner-button" v-else @click="darkModeActive = !darkModeActive">
            <ion-icon slot="icon-only" :icon="darkModeActive ? moon : moonOutline"></ion-icon>
          </ion-button>
        </template>
        <template v-slot:default>
          <div :class="['animation-container', ((showNotifications) ? 'wiggle' : undefined)]">
            <ion-icon :icon="notificationsOutline"></ion-icon>
          </div>
          <ion-label>{{ notificationCount }}</ion-label>
        </template>
      </ExtendableChip>
    </ion-buttons>

  </ion-toolbar>
</template>

<script>
import { IonToolbar, IonLabel, IonItem, IonInput, IonButtons, IonBackButton, IonButton, IonIcon, IonPopover, IonContent, IonBadge, menuController } from '@ionic/vue';
import { defineComponent, computed, watch, ref, onMounted, onUnmounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

import { moon, moonOutline, cloudOfflineOutline, notificationsOutline, informationCircleOutline, alertCircleOutline, mailOutline, at, createOutline } from 'ionicons/icons';
import hamburgerIcon from '@/assets/icons/hamburger_icon.svg';

import { useI18n } from "@/utils/i18n";

import { useDayjs } from '@/utils/dayjs';

import { apiErrorToast } from '@/utils/error';

import { iosEnterAnimation, iosLeaveAnimation } from '@/utils/custom-transition/popover';

import { verificationTimeHours, maxInputLength } from '@/config/user';

import _ from 'lodash';

import FormItem from '@/components/forms/FormItem.vue';

import ProgressButton from '@/components/ProgressButton.vue';

import CodeScannerButton from '@/components/CodeScannerButton.vue';

import ExtendableChip from '@/components/ExtendableChip.vue';

import AnimatedLogo from '@/components/AnimatedLogo.vue';

import { useRouter, onBeforeRouteLeave } from 'vue-router';

export default defineComponent({
  name: 'MainToolbar',
  components: { IonToolbar, IonLabel, IonItem, IonInput, IonButtons, IonBackButton, IonButton, IonIcon, IonPopover, IonContent, IonBadge, CodeScannerButton, ExtendableChip, AnimatedLogo, FormItem, ProgressButton },
  props: {
    isSubpage: Boolean,
    isSimple: Boolean,
    isMenuDisabled: Boolean, //Currently not used
    hideUserActions: Boolean,
    hideTitles: Boolean,
    title: String,
    showTitle: Boolean,
    titleIcon: String,
    confirmDiscard: Boolean,
    discardQuestion: String
  },
  setup(props){
    const websiteBaseURL = process.env.VUE_APP_WEBSITE_URL;

    const store = useStore();

    const router = useRouter();

    const i18n = useI18n();

    const { dayjs, dayjsLocale, isReady } = useDayjs();

    const currentTimeObject = ref(null);

    //Add two break points for the date to collapse
    const dateCollapseQuery = window.matchMedia("(max-width: 470px)");
    const dateMinSizeQuery = window.matchMedia("(max-width: 420px)");

    const collapseDate = ref(dateCollapseQuery.matches);
    const minCollapseDate = ref(dateMinSizeQuery.matches);

    const setDateCollapseFromQuery = function(query) {
      collapseDate.value = query.matches;
    }

    const setMinDateCollapseFromQuery = function(query) {
      minCollapseDate.value = query.matches;
    }

    dateCollapseQuery.addEventListener('change', setDateCollapseFromQuery);
    dateMinSizeQuery.addEventListener('change', setMinDateCollapseFromQuery);

    const currentTimeStrings = computed(() => {
      if (currentTimeObject.value != null) {
        let localDateTimeNow;
        if (isReady.value) {
          localDateTimeNow = currentTimeObject.value.locale(dayjsLocale.value);
        } else {
          localDateTimeNow = currentTimeObject.value;
        }

        let dateDayPrefix = 'dddd'; //Full day name

        if (minCollapseDate.value) {
          dateDayPrefix = ''; //Hide day name completely on smallest break point
        } else if (collapseDate.value) {
          dateDayPrefix = 'dd'; //Short day name when collapse //TODO Make 3 letters and add a dot maybe
        }

        if (dateDayPrefix.length > 0) {
          dateDayPrefix = `${dateDayPrefix}, `; //Append separator when date day is shown
        }
        
        return {
          'date': localDateTimeNow.format(`${dateDayPrefix}L`),
          'time': localDateTimeNow.format('HH:mm'),
        }
      }
      return {
        'date': null,
        'time': null
      };
    });

    const openNotifications = computed(() => {
      return store.getters['getNotifications'];
    });

    const notificationCount = computed(() => {
      return openNotifications.value.length;
    });

    const showDiscardConfirmation = ref(false);

    const discardConfirmationExtended = ref(false);

    const messagesCount = computed(() => {
      return 999; //TODO Calculate
    });

    const draftsCount = computed(() => {
      return 99; //TODO Calculate
    });

    //Only if it is a subpage inside router - otherwise warnings appear
    if (props.isSubpage) {
      onBeforeRouteLeave((newRoute) => {
        //The query param 'continue' overrides the confirm and always continues. Can be set e.g. when saving and routing away is valid.
        if (newRoute != null && newRoute.query != null && 'continue' in newRoute.query) {
          return true;
        }

        //If a confirmation is required, show it when trying to route away
        if (props.confirmDiscard) {
          //If it is already visible, we can assume the user wants to leave and discard, so continue
          if (showDiscardConfirmation.value == true) {
            //The confirmation is fully extended - prevents accidental double click to trigger it
            if (discardConfirmationExtended.value == true) {
              return true;
            }
            //Not fully extended yet, prevent routing
            return false;
          }

          //Otherwise show the confirmation
          showDiscardConfirmation.value = true;
          return false;
        }
        return true;
      });
    }

    const showNotifications = ref(false);

    //Close the other one if one opens
    watch(showDiscardConfirmation, (newStatus) => {
      if (newStatus === true) showNotifications.value = false;
    });
    watch(showNotifications, (newStatus) => {
      if (newStatus === true) showDiscardConfirmation.value = false;
    });

    watch(openNotifications, (newNotifications, oldNotifications) => {
      if (!_.isEqual(oldNotifications, newNotifications) && newNotifications.length >= 1) {
        showNotifications.value = true;
      }
    }, { immediate: true })

    //Used for simple button, current state is visualized. If pressed, preference will be set
    const darkModeActive = computed({
      get: () => store.getters.isDarkModeActive,
      set: newValue => {
        store.dispatch('setDarkModePreference', newValue);
      }
    });

    const isOffline = computed(() => {
      return !(store.getters.isOnline);
    });

    const PROCESSING_MINIMUM_DISPLAY_TIME_MS = 500;

    const processingDebounceTimeout = ref(null);
    const isApiProcessing = ref(null);
    const isApiProcessingRaw = computed(() => {
      return store.getters.isApiProcessing;
    });

    watch(isApiProcessingRaw, (isProcessing) => {
      if (isProcessing) {
        //Immediately reflect status
        if (processingDebounceTimeout.value != null) clearTimeout(processingDebounceTimeout.value);
        isApiProcessing.value = true;
      } else {
        //Delay status change to false
        if (processingDebounceTimeout.value != null) clearTimeout(processingDebounceTimeout.value);
        processingDebounceTimeout.value = setTimeout(() => {
          isApiProcessing.value = false;
        }, PROCESSING_MINIMUM_DISPLAY_TIME_MS);
      }
    });

    const handleScannedCode = function(code) {
      if (code != null && code.isInternalCode) {
        if (code.processedValue != null && code.processedValue.shortType != null && (code.processedValue.id != null || code.processedValue.uuid != null)) {
          router.push({name: 'link', params: { type: code.processedValue.shortType, id: (code.processedValue.id || code.processedValue.uuid) }});
        } //TODO Show error when not recognized?
      } else {
        console.log(code);
        //TODO If not an internal code, or if not assigned yet, ask what to do in a modal. Otherwise route there! Could also get that from the route as additional params if coming from outside.
      }
    }

    const informationState = computed(() => {
      //Only show information for users that are fully authenticated
      if (store.getters['auth/isAuthenticated']) {
        //If the user has a role that requires proof but has not yet set the proof, show warning, otherwise if provided but not yet verified show waiting message
        if (store.getters['auth/requestedRoleRequiresProof']) {
          if (!(store.getters['auth/hasRoleProofProvided'])) {
            //When already in role, show that a proof is required until a certain date has passed.
            let baseMessage = (store.getters['auth/isInRequestedRole'] ? 'missing_proof_role_required_date' : 'missing_proof');
            return { ...i18n.$t(`login.choose_role.verification_states.${baseMessage}`), proofUploadRequired: true, icon: alertCircleOutline, color: 'warning' };
          } else if (!store.getters['auth/hasRoleBeenReviewed']) {
              return i18n.$t('login.choose_role.verification_states.pending');
          }
        }
      }
      return undefined; // TODO Show draft save state while editing { title: "Entwurf lokal gespeichert", icon: save, color: 'primary' };
    });

    const isInformationPopoverOpen = ref(false);
    const informationPopoverEventRef = ref();

    const setInformationPopoverOpen = function(state, event) {
      if ( isInformationPopoverOpen.value != state ){ //Only execute change, when it needs to be changed
        informationPopoverEventRef.value = event;
        if ( state == true ){
          if ( event !== undefined ){ //Only display when it can be positioned correctly with the event
            isInformationPopoverOpen.value = true;
          }
        } else {
          isInformationPopoverOpen.value = false;
        }
      }
    }

    const roleUploadLoading = ref(false);

    const requestedRole = computed(() => {
      return store.getters['auth/getRequestedRole'];
    });

    const roleProof = ref(null);

    const signupName = ref('');

    const signupBirthname = ref('');

    const isRoleProofSet = computed(() => (roleProof.value != null));

    const isSignupNameSet = computed(() => (signupName.value != null && signupName.value.length > 0));

    const setRoleProof = function(newProof) {
      roleProof.value = newProof;
    }

    const uploadProof = async function() {
      //Proof has to be set
      if (requestedRole.value != null && roleProof.value != null && signupName.value != null && signupName.value.length > 0) {
        roleUploadLoading.value = true;
        return store.dispatch('auth/requestRole', { name: signupName.value, birthname: signupBirthname.value, requestedRole: requestedRole.value, proofFile: roleProof.value })
            .catch((error) => {
              apiErrorToast(i18n, error, true); //Do not wait for error to close with the reset of the status
            })
            .then(() => {
              roleUploadLoading.value = null;
              setInformationPopoverOpen(false);
            });
      }
    }

    const timeRefreshInterval = ref(null);

    const refreshTime = function() {
      currentTimeObject.value = dayjs();
    }

    const openSettings = function() {
      menuController.open('settings');
    }

    const showAllDraftEntries = function() {
      //TODO Get from model
      let oldestDraftDate = '01.01.2021';
      let latestDraftDate = '02.04.2026';

      if (latestDraftDate != null && oldestDraftDate != null) {
        store.commit('reports/setFilters', { 'draft': true });
        
        store.dispatch('reports/updateSelectedDay', oldestDraftDate);
        store.dispatch('reports/updateSelectedTimespanDay', latestDraftDate);

        menuController.open('side-component');
      }
    }

    onMounted(() => {
      signupName.value = store.getters['auth/getName'];
      signupBirthname.value = store.getters['auth/getBirthname'];
      refreshTime();
      if (timeRefreshInterval.value != null) {
        clearInterval(timeRefreshInterval.value);
      }
      timeRefreshInterval.value = setInterval(refreshTime, 1000);
    });

    onBeforeUnmount(() => {
      dateCollapseQuery.removeEventListener('change', setDateCollapseFromQuery);
      dateMinSizeQuery.removeEventListener('change', setMinDateCollapseFromQuery);
    });

    onUnmounted(() => {
      if (timeRefreshInterval.value != null) {
        clearInterval(timeRefreshInterval.value);
        timeRefreshInterval.value = null;
      }
    });

    return { 
      websiteBaseURL,
      i18n,
      iosEnterAnimation,
      iosLeaveAnimation,
      notificationCount,
      showDiscardConfirmation,
      discardConfirmationExtended,
      messagesCount,
      draftsCount,
      openSettings,
      currentTimeStrings,
      showNotifications,
      darkModeActive,
      isOffline,
      isApiProcessing,
      handleScannedCode,
      informationState,
      isInformationPopoverOpen,
      informationPopoverEventRef,
      setInformationPopoverOpen,
      showAllDraftEntries,
      requestedRole,
      roleProof,
      signupName,
      signupBirthname,
      isRoleProofSet,
      isSignupNameSet,
      setRoleProof,
      uploadProof,
      roleUploadLoading,
      verificationTimeHours,
      maxInputLength,
      moon,
      moonOutline,
      cloudOfflineOutline,
      notificationsOutline,
      informationCircleOutline,
      mailOutline,
      at,
      hamburgerIcon,
      createOutline
    };
  }
});
</script>

<style>
/*popover styles cannot be scoped!*/
.information-addon-popover {
  --width: min(400px, 90vw);
}

.information-addon-popover-content > ion-label {
  text-align: center;
  display: block!important;
  margin-block: 1em;
}

.information-addon-popover-content::part(scroll) {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.split-pane-visible .main-menu #offline-icon, .menu-content:not(.split-pane-visible) #main #offline-icon, .no-valid-side-menu #main #offline-icon {
  display: block!important;
}

.split-pane-visible .main-menu .logo-button, .menu-content:not(.split-pane-visible) #main .logo-button, .no-valid-side-menu #main .logo-button {
  display: block!important;
}

.valid-side-menu.split-pane-visible #main .fallback-title {
  display: none;
}
</style>

<style scoped>
ion-toolbar {
  display: flex;
  height: calc(3em + var(--ion-safe-area-top, 0px));
  align-items: center;
  justify-content: center;
  --padding-offset: 12px;
  padding-left: calc(var(--ion-safe-area-left) + var(--padding-offset) + 3px);
  padding-right: calc(var(--ion-safe-area-right) + var(--padding-offset));
}

#time-title, #date-title {
  color: var(--ion-toolbar-contrast-color);
}

.date-time-separator {
  color: var(--ion-toolbar-contrast-color);
  margin-inline: 10px;
}

#time-title {
  font-family: var(--ion-mono-font);
}

.nav-buttons {
  height: 100%;
  position: relative;
}

.nav-buttons > *:last-child {
  margin-right: 10px;
}

.logo-button {
  display: none;
  flex-grow: 0;
  height: 100%;
  --padding-start: 0px;
  --padding-end: 0px;
  margin: 0px;
}
.logo {
  flex-grow: 1;
  font-size: 2rem;
  color: var(--ion-toolbar-highlight-color);
}

.title-icon {
  height: 100%;
  vertical-align: middle;
  display: inline;
  padding-left: 5px;
  padding-right: 5px;
}

#title ion-label {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
}

#title {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 16px;
  row-gap: 2px;
  position: relative;
  color: var(--ion-toolbar-contrast-color);
  overflow: hidden;
}

#title ion-label {
  padding: 0px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-right: auto;
}

.custom-title {
  padding: 0px!important;
  font-size: var(--level-l1-font-size)!important;
  font-weight: var(--weight-level-semibold)!important;
}

/* This makes the time collapse into the next line (from the left position), if not enough space. As it is just one line high, the time will be invisble. */
#title ion-label.fallback-title {
  height: 1.5em;
  line-height: 1.5em;
  display: flex;
  flex-flow: row wrap;
  direction: rtl;
  justify-content: flex-end;
  overflow: hidden;
}

#title ion-label * {
  direction: unset;
}

#offline-icon {
  display: none;
  margin-left: 13px;
  margin-right: 3px;
  font-size: 1em;
  color: var(--ion-color-tertiary, violet);
}

.back-button, .menu-button-notification {
  margin: 0px;
  padding: 0px;
}

.back-button .inner-button:not(.ios) {
  color: var(--ion-toolbar-contrast-color, #000);
}

.back-button.extended .inner-button:not(.ios) {
  color: var(--ion-color-contrast, #fff);
} 

.menu-button-notification .inner-button {
  --padding-start: 0px;
  --padding-end: 0px;
  color: var(--ion-toolbar-highlight-color);
}

.menu-button-notification.opaque-when-closed .inner-button, .menu-button-notification.extended .inner-button {
  color: var(--ion-color-primary-contrast);
}

.menu-button-notification {
  height: 36px!important; /* 2*2 px larger then the inner button because of border */
}

.menu-button-notification .inner-button.menu {
  width: 32px;
  height: 32px;
}

.inner-button {
  pointer-events: none;
}

@keyframes bell {
  0% { transform: rotate(0); }
  15% { transform: rotate(15deg); }
  30% { transform: rotate(-15deg); }
  45% { transform: rotate(10deg); }
  60% { transform: rotate(-10deg); }
  75% { transform: rotate(5deg); }
  85% { transform: rotate(-5deg); }
  92% { transform: rotate(2deg); }
  100% { transform: rotate(0); }
}

.animation-container {
  vertical-align: bottom;
  display: flex;
  align-items: center;
  margin-left: 5px;
  font-size: 0.55em;
}

.animation-container.wiggle {
  animation: bell 1s cubic-bezier(.36,.07,.19,.97) 2 both;
}

.discard-label {
  white-space: nowrap;
  margin-right: 5px;
}

.ios .discard-label {
  margin-left: 5px;
}

#messages-button, #drafts-button {
  font-size: 0.8em;
  width: auto;
  min-width: 32px;
  height: 36px;
  --padding-top: 0px;
  --padding-bottom: 0px;
  --padding-start: 0px;
  --padding-end: 0px;
  --border-radius: 6px;
  
  --color: var(--ion-toolbar-highlight-color);
}

#messages-button {
  --padding-top: 2px;
  margin-right: 3px;
}

#drafts-button {
  margin-right: 8px;
  --padding-bottom: 1px;
}

#messages-button ion-icon, #drafts-button ion-icon {
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  font-size: 1.8em;
}

#drafts-button ion-icon {
  font-size: 1.9em;
}

#messages-button .no-badge, #drafts-button .no-badge {
  visibility: hidden;
}

#messages-button ion-badge, #drafts-button ion-badge {
  min-width: 16px;
  z-index: 100;
  font-size: 0.9em;
  --padding-start: 4px;
  --padding-end: 4px;
  --padding-top: 2px;
  --padding-bottom: 2px;
  margin-left: 2px;
}

#messages-button ion-badge {
  margin-right: -7px;
  margin-top: -18px;
}

#drafts-button ion-badge {
  margin-right: -8px;
  margin-top: -16px;
}

.code-scanner-button {
  --icon-font-size: 1.75em;
  --icon-padding: 0.8em;
  --button-padding-inline: 3px;
  --border-radius: 6px!important;
  margin-left: 0px;
  margin-right: 8px;
  --color: var(--ion-toolbar-highlight-color);
}

.information-addon {
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  align-items: center;
  margin-right: auto;
  text-align: center;
  background: var(--ion-toolbar-background)!important;
  padding-block: 0px;
}

.information-addon a {
  display: inline-flex;
  align-items: center;
  font-size: var(--level-s2-font-size);
  overflow: hidden;
  gap: 1ch;
  text-transform: none;
  text-decoration: none;
}

.information-addon a ion-icon {
  flex-shrink: 0;
  font-size: 1.2em;
}

.proof-file-upload {
  background: transparent;
  --background: transparent;
  --border-width: 1px;
  --border-style: solid;
  --border-radius: 4px;
  --border-color: var(--ion-color-primary);
  margin-block: 1em;
}

.upload-proof-confirm-button {
  width: 100%;
  margin-top: 10px;
  margin-bottom: 10px;
  height: 3em;
  font-size: var(--level-0-font-size);
  font-weight: var(--weight-level-default);
  white-space: normal;
  text-transform: none;
}

.name-upload-proof-item {
  max-width: 300px;
  width: 100%;
}

#upload-proof-verfication-time {
  margin-bottom: 0.25em;
}

#upload-proof-contact {
  margin-top: 0px;
  max-width: 300px;
}

.inline-icon {
  vertical-align: -0.2em;
}

a:hover, a:active {
  text-decoration: underline;
}

a {
  text-decoration: none;
}
</style>
